import React, { useEffect } from 'react';

// "About shipping prices" explainer, opened from the label step on
// TradeProgress and PurchaseProgress. Members regularly email support saying
// their own Pirate Ship account is cheaper than a PXC label; this answers that
// in place. It is a modal, not a page, so nobody loses their place in the
// progress flow or the half-filled label form behind it.
//
// Copy rules (owner, 2026-10-21): name Pirate Ship explicitly, since that is
// what members compare against; justify what the price includes, never say or
// imply PXC labels cost more; never mention the PXC label platform fee
// (functions/labelPricing.js). The Pirate Ship facts ($100 built-in USPS cover
// on most labels, third-party insurance above that, only the shipper can file
// an InsureShield claim) come from Pirate Ship's own support pages.
//
// variant: 'trade' | 'purchase'. On a purchase only the seller buys a label,
// but the buyer can open this from the same step, so the claims paragraph is
// written to whichever side is reading (isBuyer). The trading savings section
// is omitted on purchases.
const ShippingPricesModal = ({ isOpen, onClose, variant = 'trade', isBuyer = false }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isPurchase = variant === 'purchase';

  const headingStyle = { margin: '20px 0 6px', fontSize: '16px', fontWeight: 700, color: '#1f2937' };
  const paraStyle = { margin: '0 0 10px', fontSize: '14px', color: '#4b5563', lineHeight: 1.55 };

  return (
    // data-pull-refresh-opt-out: the progress page behind this is usually
    // scrolled to the top, so on native a drag down to scroll this text back
    // up would read as a pull and reload the page, the same bug the
    // notification panel had (2026-10-20; see hooks/usePullToRefresh.js).
    <div
      data-pull-refresh-opt-out=""
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px'
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shipping-prices-title"
        style={{
          backgroundColor: '#fff', borderRadius: '16px', padding: '20px 22px 22px',
          maxWidth: '480px', width: '100%', maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain',
          fontFamily: 'Nunito, sans-serif', boxSizing: 'border-box', textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <h3 id="shipping-prices-title" style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#1f2937' }}>
            About shipping prices
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0, width: '32px', height: '32px', borderRadius: '50%',
              border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280',
              fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>

        <h4 style={{ ...headingStyle, marginTop: '14px' }}>Full-value insurance is included.</h4>
        <p style={paraStyle}>
          Every PXC label includes discounted postage and insurance for your pedal's full transaction value.{' '}
          <strong style={{ color: '#1f2937' }}>PXC labels are required</strong> so we can manage claims, track delivery, and keep your transaction protected.
        </p>
        <p style={paraStyle}>
          Most USPS labels on Pirate Ship include only up to $100 of coverage; higher-value shipments require additional 3rd party insurance. We compared alternatives with equivalent coverage, and they did not offer lower pricing than our current setup.
        </p>

        <h4 style={headingStyle}>We can help when something goes wrong.</h4>
        <p style={paraStyle}>
          PXC holds the policy and files claims on your behalf if a package is lost or damaged.
        </p>
        <p style={paraStyle}>
          {isPurchase && isBuyer
            ? "If the seller shipped on their own Pirate Ship label, even with full-value insurance, only they could file a claim, and we couldn't step in for you. Our labels let us stay involved when you need help."
            : isPurchase
            ? "If you shipped on your own Pirate Ship label, even with full-value insurance, only you could file a claim, and we couldn't step in for you or the buyer. Our labels let us stay involved when you need help."
            : "If your trade partner bought their own Pirate Ship label, even with full-value insurance, only they could file an insurance claim. You'd be relying on them to pursue it. Our labels let us stay involved when you need help."}
        </p>

        <h4 style={headingStyle}>{isPurchase ? 'This label connects the whole purchase.' : 'This label connects the whole trade.'}</h4>
        <p style={paraStyle}>
          {isPurchase
            ? 'PXC tracking confirms delivery and releases payment to the seller 24 hours after the pedal shows as delivered. PXC labels are also required for TradeProtect eligibility.'
            : 'PXC tracking confirms delivery, completes your trade, and releases any cash payment 24 hours after both pedals show as delivered. PXC labels are also required for TradeProtect eligibility.'}
        </p>
        <p style={paraStyle}>
          Outside labels would disconnect these safeguards and put claims beyond our control.
        </p>

        {!isPurchase && (
          <>
            <h4 style={headingStyle}>Get more out of your gear.</h4>
            <p style={paraStyle}>
              Selling and buying the equivalent pedals on Reverb means selling fees, processing fees, and shipping across multiple transactions. Trading avoids that cycle: each member pays for only one outgoing label and one fixed trade fee.
            </p>
          </>
        )}

        <p style={{ ...paraStyle, marginTop: '16px', fontSize: '13px', fontStyle: 'italic', color: '#6b7280' }}>
          Insurance is subject to policy terms. TradeProtect is separate and optional.
        </p>

        <div style={{ textAlign: 'center', marginTop: '18px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: '#C9A227', color: '#fff', border: 'none',
              borderRadius: '25px', padding: '10px 32px', fontSize: '15px',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// Inline text link that opens the modal. Blue because it is actionable,
// matching the "How to trade" link on the listing page.
export const ShippingPricesLink = ({ onClick, style }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'none', border: 'none', padding: 0, margin: 0,
      color: '#5A9ECB', textDecoration: 'underline', fontWeight: 800,
      fontSize: '1.08em', fontFamily: 'inherit', cursor: 'pointer',
      whiteSpace: 'nowrap', ...style
    }}
  >
    About shipping prices
  </button>
);

export default ShippingPricesModal;
